import ReactECharts from 'echarts-for-react';
import { useTranslate } from 'react-admin';
import { crossSymbol } from '../../../../assets/crossSymbol';
import globalUseStyles from '../../../../styles/globalCustomStyles';
import ChartLoading from '../../../aegeon/charts/ChartLoading';
import ChartNoData from '../../../aegeon/charts/ChartNoData';
import { getColors } from '../../../aegeon/compute_performances';
import { is_value_defined } from '../../../aegeon/helper';

const SiteEnergeticPerformanceDPEGraphMax = (props) => {
  const globalClasses = globalUseStyles();
  const t = useTranslate();

  const { type, data, isLoading, dpeTable, colors: energy_colors } = props

  const perf_max = data?.performances?.find((p) => p.type === "DPE_ENEP");
  const perf = type !== "DPE_MAX" ? data?.performances?.find((p) => p.type === type) : perf_max;
  const value = is_value_defined(perf?.value) ? Math.round(perf?.value * 100) / 100 : null;

  const perf_energy = data?.performances?.find((p) => p.type === "DPE_ENEP");
  const perf_ghg = data?.performances?.find((p) => p.type === "DPE_GHG");
  const energy_unit = perf_energy?.unit;
  const ghg_unit = perf_ghg?.unit;

  const colors = getColors(type)

  const gradeBounds = [
    { grade: "A", energyMax: dpeTable?.energy_a, ghgMax: dpeTable?.ghg_a, color: colors["A"] },
    { grade: "B", energyMax: dpeTable?.energy_b, ghgMax: dpeTable?.ghg_b, color: colors["B"] },
    { grade: "C", energyMax: dpeTable?.energy_c, ghgMax: dpeTable?.ghg_c, color: colors["C"] },
    { grade: "D", energyMax: dpeTable?.energy_d, ghgMax: dpeTable?.ghg_d, color: colors["D"] },
    { grade: "E", energyMax: dpeTable?.energy_e, ghgMax: dpeTable?.ghg_e, color: colors["E"] },
    { grade: "F", energyMax: dpeTable?.energy_f, ghgMax: dpeTable?.ghg_f, color: colors["F"] },
    { grade: "G", energyMax: dpeTable?.energy_g || 9999, ghgMax: dpeTable?.ghg_g || 9999, color: colors["G"] },
  ];

  const getGrade = (value, type) => {
    if (!is_value_defined(value)) return null;
    for (let i = 0; i < gradeBounds.length; i++) {
      const bound = gradeBounds[i];
      if (type === "energy" && value <= bound.energyMax) return bound.grade;
      if (type === "ghg" && value <= bound.ghgMax) return bound.grade;
    }
    return "G";
  };

  const energyGrade = getGrade(perf_energy?.value, "energy");
  const ghgGrade = getGrade(perf_ghg?.value, "ghg");

  const gradeOrder = ["A", "B", "C", "D", "E", "F", "G"];
  const maxGrade = gradeOrder[
    Math.max(
      gradeOrder.indexOf(energyGrade || "G"),
      gradeOrder.indexOf(ghgGrade || "G")
    )
  ];
  const maxGradeType = maxGrade === energyGrade ? "energy" : "ghg";
  const color = energy_colors[maxGradeType] || '#6c74d8';

  const stretchValue = (val, breaks) => {
    let prev = 0, scaled = 0;
    for (let i = 0; i < breaks.length; i++) {
      const { end, factor } = breaks[i];
      if (val <= end) {
        scaled += (val - prev) * factor;
        return scaled;
      }
      scaled += (end - prev) * factor;
      prev = end;
    }
    return scaled + (val - prev);
  };

  const energyBreaks = [
    { end: dpeTable?.energy_a, factor: 2 },
    { end: dpeTable?.energy_b, factor: 2 },
    { end: dpeTable?.energy_g || 9999, factor: 1 }
  ];
  const ghgBreaks = [
    { end: dpeTable?.ghg_a, factor: 2 },
    { end: dpeTable?.ghg_b, factor: 2 },
    { end: dpeTable?.ghg_g || 9999, factor: 1 }
  ];

  let prevEnergy = 0, prevGhg = 0;
  const markAreas = [];
  gradeBounds.forEach((bound) => {
    if (bound.energyMax && bound.ghgMax) {
      const sPrevEnergy = stretchValue(prevEnergy, energyBreaks);
      const sPrevGhg = stretchValue(prevGhg, ghgBreaks);
      const sEnergyMax = stretchValue(bound.energyMax, energyBreaks);
      const sGhgMax = stretchValue(bound.ghgMax, ghgBreaks);

      markAreas.push([
        {
          coord: [sPrevGhg, 0],
          itemStyle: {
            color: bound.grade === maxGrade ? bound.color : bound.color,
            opacity: bound.grade === maxGrade ? 1 : 0.25
          },
        },
        {
          coord: [sGhgMax, sPrevEnergy]
        }
      ]);
      markAreas.push([
        {
          coord: [0, sPrevEnergy],
          itemStyle: {
            color: bound.grade === maxGrade ? bound.color : bound.color,
            opacity: bound.grade === maxGrade ? 1 : 0.25
          },
          label: {
            show: true,
            color: bound.grade === maxGrade && maxGrade === "G" ? 'white' : '#333',
            position: 'insideTopRight',
            fontWeight: 'bold',
            formatter: bound.grade,
            fontSize: 16,
            opacity: 1
          }
        },
        {
          coord: [sGhgMax, sEnergyMax]
        }
      ]);
      prevEnergy = bound.energyMax;
      prevGhg = bound.ghgMax;
    }
  });

  const energyMax = stretchValue(dpeTable?.energy_f + (dpeTable?.energy_f - dpeTable?.energy_e), energyBreaks);
  const ghgMax = stretchValue(dpeTable?.ghg_f + (dpeTable?.ghg_f - dpeTable?.ghg_e), ghgBreaks);

  const option = {
    title: {
      text: maxGrade ? t("sites.energetic_performance.DPE_MAX.max_note") + ' : ' + maxGrade : '',
      left: 'center',
      top: 0,
      textStyle: {
        fontSize: 18,
        fontWeight: 'bold',
        fontFamily: 'Montserrat, sans-serif',
        color: color
      }
    },
    tooltip: {
      trigger: 'axis',
      show: true,
      confine: true,
      alwaysShowContent: false,
      renderMode: 'html',
      formatter: function (params) {
        if (!params || !params[0] || !params[0].value) return '';
        const p = params[0];
        let x = p.value[0];
        let y = p.value[1];

        let ghg = null;
        if (x <= stretchValue(dpeTable?.ghg_a, ghgBreaks)) {
          ghg = Math.round(x / 2);
        } else if (x <= stretchValue(dpeTable?.ghg_b, ghgBreaks)) {
          const x0 = stretchValue(dpeTable?.ghg_a, ghgBreaks);
          ghg = Math.round((x - x0) / 2 + dpeTable?.ghg_a);
        } else {
          const x0 = stretchValue(dpeTable?.ghg_b, ghgBreaks);
          ghg = Math.round(x - x0 + dpeTable?.ghg_b);
        }

        let energy = null;
        if (y <= stretchValue(dpeTable?.energy_a, energyBreaks)) {
          energy = Math.round(y / 2);
        } else if (y <= stretchValue(dpeTable?.energy_b, energyBreaks)) {
          const y0 = stretchValue(dpeTable?.energy_a, energyBreaks);
          energy = Math.round((y - y0) / 2 + dpeTable?.energy_a);
        } else {
          const y0 = stretchValue(dpeTable?.energy_b, energyBreaks);
          energy = Math.round(y - y0 + dpeTable?.energy_b);
        }
        let hoveredGrade = null;
        if (energy !== null && ghg !== null) {
          let grade = "G";
          for (let i = 0; i < gradeBounds.length; i++) {
            const bound = gradeBounds[i];
            if (energy <= bound.energyMax && ghg <= bound.ghgMax) {
              grade = bound.grade;
              break;
            }
          }
          hoveredGrade = grade;
        }
        let energyNote = "G";
        let ghgNote = "G";
        for (let i = 0; i < gradeBounds.length; i++) {
          const bound = gradeBounds[i];
          if (energy !== null && energy <= bound.energyMax && energyNote === "G") {
            energyNote = bound.grade;
          }
          if (ghg !== null && ghg <= bound.ghgMax && ghgNote === "G") {
            ghgNote = bound.grade;
          }
        }
        return `
          <div>
            <b style="color:${energy_colors?.ghg || '#6c74d8'}">${t('sites.energetic_performance.DPE_MAX.carbon')} : ${ghgNote}</b> - ${ghg} ${ghg_unit}<br/>
            <b style="color:${energy_colors?.energy || '#5da4d9'}">${t('sites.energetic_performance.DPE_MAX.energy')} : ${energyNote}</b> - ${energy} ${energy_unit}<br/>
            <b>${t('sites.energetic_performance.DPE_MAX.max_note')} : ${hoveredGrade || ''}</b>
          </div>
        `;
      }
    },
    grid: {
      left: '10%',
      right: '5%',
      bottom: '20%',
      top: '10%',
      show: false,
    },
    xAxis: {
      name: `${t('sites.energetic_performance.DPE_MAX.carbon')} (${ghg_unit})`,
      nameLocation: 'middle',
      nameGap: 20,
      nameTextStyle: {
        fontSize: 12,
        fontFamily: 'Montserrat, sans-serif',
        color: energy_colors?.ghg || '#6c74d8'
      },
      type: 'value',
      min: 0,
      max: ghgMax,
      splitLine: { show: false },
      axisLabel: {
        color: energy_colors?.ghg || '#6c74d8',
        formatter: (val) => {
          let v = Number(val);
          if (v <= stretchValue(dpeTable?.ghg_a, ghgBreaks)) {
            return Math.round(v / 2);
          }
          if (v <= stretchValue(dpeTable?.ghg_b, ghgBreaks)) {
            return Math.round((v - stretchValue(dpeTable?.ghg_a, ghgBreaks)) / 2 + dpeTable?.ghg_a);
          }
          return Math.round(v - stretchValue(dpeTable?.ghg_b, ghgBreaks) + dpeTable?.ghg_b);
        }
      },
      axisLine: {
        show: true,
        symbol: ['none', 'arrow'],
        symbolSize: [12, 16],
        lineStyle: {
          color: energy_colors?.ghg || '#6c74d8',
          width: 2
        }
      },
    },
    yAxis: {
      name: `${t('sites.energetic_performance.DPE_MAX.energy')} (${energy_unit})`,
      nameLocation: 'middle',
      nameGap: 25,
      nameTextStyle: {
        fontSize: 12,
        fontFamily: 'Montserrat, sans-serif',
        color: energy_colors?.energy || '#5da4d9'
      },
      type: 'value',
      min: 0,
      max: energyMax,
      splitLine: { show: false },
      axisLabel: {
        rotate: 90,
        color: energy_colors?.energy || '#5da4d9',
        formatter: (val) => {
          let v = Number(val);
          if (v <= stretchValue(dpeTable?.energy_a, energyBreaks)) {
            return Math.round(v / 2);
          }
          if (v <= stretchValue(dpeTable?.energy_b, energyBreaks)) {
            return Math.round((v - stretchValue(dpeTable?.energy_a, energyBreaks)) / 2 + dpeTable?.energy_a);
          }
          return Math.round(v - stretchValue(dpeTable?.energy_b, energyBreaks) + dpeTable?.energy_b);
        }
      },
      axisLine: {
        show: true,
        symbol: ['none', 'arrow'],
        symbolSize: [12, 16],
        lineStyle: {
          color: energy_colors?.energy || '#5da4d9',
          width: 2
        }
      },
    },
    series: [
      {
        type: 'scatter',
        symbol: crossSymbol,
        symbolSize: 12,
        data: (perf_energy?.value && perf_ghg?.value)
          ? [[stretchValue(perf_ghg.value, ghgBreaks), stretchValue(perf_energy.value, energyBreaks)]]
          : [],
        itemStyle: { color: '#000000' },
        markArea: {
          silent: true,
          data: markAreas
        }
      }
    ]
  };

  return (
    isLoading ?
      <ChartLoading /> :
      perf && value !== undefined && value !== null ?
        <div className={globalClasses.chartDPE}>
          <ReactECharts
            option={option}
            notMerge={true}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
        :
        <ChartNoData />
  )
}

export default SiteEnergeticPerformanceDPEGraphMax